import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { fmtVND } from '../utils/format';
import { listRefunds, createRefunds, approveRefund, rejectRefund, deleteRefund, applyRefunds, reassignRefund, setRefundReduction, payrollScanStatus } from '../api/refunds';
import { searchCustomers } from '../api/customers';

// Ô tìm & chọn KH thuộc quyền quản lý (mine=1). Chọn 1 gợi ý -> điền mã + tên; gõ tự do vẫn được.
function CustomerPicker({ code, name, onPick }) {
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const [pos, setPos] = useState(null); // toạ độ fixed của dropdown (thoát khỏi overflow của bảng)
  const place = () => { const r = inputRef.current?.getBoundingClientRect(); if (r) setPos({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 240) }); };
  useEffect(() => {
    const t = q.trim();
    if (!t) { setOpts([]); return; }
    const id = setTimeout(() => searchCustomers(t, true).then(setOpts).catch(() => {}), 250);
    return () => clearTimeout(id);
  }, [q]);
  if (code || name) {
    return (
      <span className="row" style={{ gap: 4, flexWrap: 'nowrap', alignItems: 'center' }}>
        <span className="tag" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${code || ''} ${name || ''}`.trim()}>
          {code ? <code style={{ color: 'var(--warn)' }}>{code}</code> : null} {name}
        </span>
        <button className="btn sm ghost" type="button" style={{ flex: 'none' }} onClick={() => onPick({ custCode: '', custName: '' })}>✕</button>
      </span>
    );
  }
  return (
    <>
      <input ref={inputRef} value={q}
        onChange={(e) => { setQ(e.target.value); place(); setOpen(true); }}
        onFocus={() => { place(); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Tìm mã/tên KH…" style={{ width: '100%', boxSizing: 'border-box' }} />
      {open && q.trim() && pos && (
        <div className="refund-cust-pop" style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width }}>
          {opts.length ? opts.map((o) => (
            <div key={o.id} className="refund-cust-item" title={`${o.code} ${o.name}`} onMouseDown={() => { onPick({ custCode: o.code, custName: o.name }); setQ(''); setOpen(false); }}>
              <code style={{ color: 'var(--warn)' }}>{o.code}</code> {o.name}{o.sale ? <span className="small muted"> · {o.sale}</span> : null}
            </div>
          )) : (
            <div className="refund-cust-item" onMouseDown={() => { onPick({ custCode: '', custName: q.trim() }); setQ(''); setOpen(false); }}>
              <span className="small muted">Không thấy — dùng tên “{q.trim()}”</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

const TARGETS = [
  { v: 'congMua', l: 'Công mua' }, { v: 'tienHang', l: 'Tiền hàng' }, { v: 'cuoc', l: 'Tiền cước' },
  { v: 'phuThu', l: 'Phụ thu' }, { v: 'khac', l: 'Khác' },
];
const TLABEL = Object.fromEntries(TARGETS.map((t) => [t.v, t.l]));
const REASON_MIN_H = 86; // ô "Lý do hoàn": cao tối thiểu ~4 dòng
const PCT = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const KG = [5, 10, 15, 20, 25, 30];
const thisMonth = () => new Date().toISOString().slice(0, 7);
// Ngày làm đề xuất (dd/mm/yyyy) — chi tiết hơn cột "tháng" cũ.
const fmtDay = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  if (isNaN(t)) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(t.getDate())}/${p(t.getMonth() + 1)}/${t.getFullYear()}`;
};
const emptyRow = () => ({ custCode: '', custName: '', orderCode: '', wholeMonth: false, month: thisMonth(), reason: '', amount: '', reduceTarget: 'tienHang', reduceRate: 0, account: '' });

const STATUS = {
  pending_kd: { l: 'Chờ TP KD', c: 'var(--warn)' },
  pending_dvkh: { l: 'Chờ TP DVKH', c: 'var(--accent)' },
  pending_ketoan: { l: 'Chờ Kế toán', c: 'var(--accent2)' },
  approved: { l: 'Đã duyệt', c: 'var(--ok)' },
  rejected: { l: 'Từ chối', c: 'var(--danger)' },
};

// Ô nhập SỐ GIẢM TRỪ (VND) — lưu khi rời ô / Enter, chỉ gọi API khi số thực sự đổi.
function MoneyCell({ value, onSave, extra }) {
  const [v, setV] = useState(value ?? 0);
  useEffect(() => setV(value ?? 0), [value]);
  const commit = () => { const n = Math.max(0, Number(v) || 0); if (n !== Number(value)) onSave(n); };
  return (
    <div>
      <input
        type="number" value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
        style={{ width: '100%', boxSizing: 'border-box', textAlign: 'right', fontFamily: 'var(--font-mono)' }}
      />
      {extra}
    </div>
  );
}

export default function RefundProposalPage() {
  const { me } = useAuth();
  const toast = useToast();
  const [data, setData] = useState({ items: [], canSeeAll: false, isKeToanHead: false });
  const [drafts, setDrafts] = useState([emptyRow()]);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [applyMonth, setApplyMonth] = useState(''); // tháng kế toán chọn để giảm trừ
  const [applyScan, setApplyScan] = useState('loading'); // trạng thái quét doanh thu tháng đó
  const [applyPick, setApplyPick] = useState(() => new Set()); // id các đề xuất được tích
  const [applyResult, setApplyResult] = useState(null); // kết quả sau khi giảm trừ
  const [detail, setDetail] = useState(null); // đề xuất đang xem chi tiết "đã trừ đơn nào"
  const [filter, setFilter] = useState('all'); // all | undone (chưa giảm trừ) | done (đã giảm trừ)
  // Lọc theo TRẠNG THÁI DUYỆT ('' = tất cả) — TP nào cũng lọc được "Chờ TP KD/DVKH/Kế toán…"
  // để soát nhanh các đề xuất đến lượt mình duyệt. Bấm lại nút đang chọn để bỏ lọc.
  const [statusFilter, setStatusFilter] = useState('');

  const load = () => listRefunds().then(setData).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const heads = me.deptHeadOf || [];
  const isAdmin = me.role === 'GD' || me.role === 'CV';
  function canApprove(p) {
    if (['approved', 'rejected'].includes(p.status)) return false;
    if (isAdmin) return true;
    if (p.status === 'pending_kd') return me.role === 'TP' && p.group && String(p.group) === String(me.group?.id || me.group);
    if (p.status === 'pending_dvkh') return heads.includes('Phòng DVKH');
    if (p.status === 'pending_ketoan') return (me.departments || []).includes('Phòng kế toán');
    return false;
  }

  function setDraft(i, patch) {
    setDrafts((ds) => {
      const next = ds.map((d, j) => (j === i ? { ...d, ...patch } : d));
      const last = next[next.length - 1];
      if (last.custCode.trim() || last.amount) next.push(emptyRow()); // tự thêm dòng khi điền hết
      return next;
    });
  }
  const removeDraft = (i) => setDrafts((ds) => (ds.length > 1 ? ds.filter((_, j) => j !== i) : [emptyRow()]));

  async function submit() {
    const rows = drafts.filter((d) => d.custCode.trim() || d.amount).map((d) => ({
      custCode: d.custCode, custName: d.custName, wholeMonth: d.wholeMonth, orderCode: d.orderCode,
      month: d.month, // "Cả tháng": tháng chọn. Đơn lẻ: server ưu tiên tháng của đơn đã quét, đây chỉ là dự phòng.
      reason: d.reason, amount: d.amount, reduceTarget: d.reduceTarget, reduceRate: d.reduceRate, account: d.account,
    }));
    if (!rows.length) { toast('Chưa có dòng nào để gửi', 'danger'); return; }
    setBusy(true);
    try {
      const r = await createRefunds(rows);
      if (r.count) toast(`Đã gửi ${r.count} đề xuất${r.errors?.length ? ` · ${r.errors.length} dòng bị bỏ` : ''}`);
      (r.errors || []).slice(0, 3).forEach((m) => toast(m, 'danger'));
      setDrafts([emptyRow()]); load();
    }
    catch (e) { toast(e.response?.data?.message || 'Gửi thất bại', 'danger'); }
    finally { setBusy(false); }
  }
  // Kế toán (hoặc GĐ) được sửa NƠI + SỐ giảm trừ TRƯỚC khi duyệt, khi chưa giảm trừ.
  const isAccountant = isAdmin || (me.departments || []).includes('Phòng kế toán');
  const canEditReduction = (p) => isAccountant && !p.applied && p.status !== 'rejected';
  async function saveReduction(p, patch) {
    const reduceTarget = patch.reduceTarget ?? p.reduceTarget;
    let reduceRate = patch.reduceRate ?? p.reduceRate;
    if (!(reduceTarget === 'congMua' || reduceTarget === 'cuoc')) reduceRate = 0;
    // reduceAmount: chỉ gửi khi có (ô nhập tiền của tiền hàng/phụ thu/cước-số-tiền).
    const reduceAmount = patch.reduceAmount;
    try { await setRefundReduction(p.id, reduceTarget, reduceRate, reduceAmount); load(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi', 'danger'); }
  }
  async function doApprove(p) { try { await approveRefund(p.id); toast('Đã duyệt'); load(); } catch (e) { toast(e.response?.data?.message || 'Lỗi', 'danger'); } }
  async function doReject(p) { const reason = window.prompt('Lý do từ chối:'); if (reason === null) return; try { await rejectRefund(p.id, reason); toast('Đã từ chối'); load(); } catch (e) { toast(e.response?.data?.message || 'Lỗi', 'danger'); } }
  async function doDelete(p) { if (!window.confirm('Xóa đề xuất này?')) return; try { await deleteRefund(p.id); load(); } catch (e) { toast(e.response?.data?.message || 'Lỗi', 'danger'); } }
  // Các tháng CÓ đề xuất đã duyệt & chưa giảm trừ (để kế toán chọn tháng nào cần trừ).
  const applyMonths = [...new Set(data.items.filter((p) => p.status === 'approved' && !p.applied).map((p) => p.month))].sort().reverse();
  // Đề xuất đã duyệt & chưa trừ của tháng đang chọn.
  const applyRows = data.items.filter((p) => p.status === 'approved' && !p.applied && p.month === applyMonth);

  async function openApply() {
    const m = applyMonths[0] || thisMonth();
    setApplyMonth(m);
    setApplyResult(null);
    setConfirmChecked(false);
    setConfirmOpen(true);
    await loadApplyMonth(m);
  }
  async function loadApplyMonth(m) {
    setApplyMonth(m);
    // Tích sẵn TẤT CẢ đề xuất của tháng đó; kế toán bỏ tích cái không muốn trừ.
    setApplyPick(new Set(data.items.filter((p) => p.status === 'approved' && !p.applied && p.month === m).map((p) => p.id)));
    setApplyScan('loading');
    try { const s = await payrollScanStatus(m); setApplyScan(s?.status || 'idle'); }
    catch { setApplyScan('idle'); }
  }
  const togglePick = (id) => setApplyPick((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function doApply() {
    if (!applyMonth) return;
    setBusy(true);
    try {
      const ids = [...applyPick];
      const r = await applyRefunds(true, applyMonth, ids);
      setApplyResult(r);
      toast(`Tháng ${applyMonth}: đã giảm trừ ${r.applied} đề xuất${r.skipped?.length ? ` · bỏ qua ${r.skipped.length}` : ''}`);
      load();
    } catch (e) { toast(e.response?.data?.message || 'Giảm trừ thất bại', 'danger'); }
    finally { setBusy(false); }
  }
  async function doReassign(p) {
    const orderCode = window.prompt('Chỉ định mã đơn khác trong tháng để giảm trừ (bỏ trống = Cả tháng):', p.orderCode || '');
    if (orderCode === null) return;
    try { await reassignRefund(p.id, orderCode.trim(), !orderCode.trim()); toast('Đã chỉ định lại đơn'); load(); }
    catch (e) { toast(e.response?.data?.message || 'Lỗi', 'danger'); }
  }

  const rateCell = (d, i, disabled) => {
    const s = { width: '100%', boxSizing: 'border-box' };
    if (d.reduceTarget === 'congMua') return <select value={d.reduceRate} disabled={disabled} style={s} onChange={(e) => setDraft(i, { reduceRate: Number(e.target.value) })}><option value={0}>%</option>{PCT.map((p) => <option key={p} value={p}>{p}%</option>)}</select>;
    if (d.reduceTarget === 'cuoc') return <select value={d.reduceRate} disabled={disabled} style={s} onChange={(e) => setDraft(i, { reduceRate: Number(e.target.value) })}><option value={0}>k/kg</option>{KG.map((p) => <option key={p} value={p}>{p}k/kg</option>)}</select>;
    return <span className="small muted">0</span>;
  };
  const th = { padding: '8px 7px', textAlign: 'left', color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const thR = { ...th, textAlign: 'right' };
  const td = { padding: '6px 7px', verticalAlign: 'middle' };
  const tdR = { ...td, textAlign: 'right' };
  const inp = { width: '100%', boxSizing: 'border-box' }; // ô nhập lấp đầy cột
  // Bề rộng cột cố định để tiêu đề & ô nhập thẳng hàng (table-layout: fixed).
  // Thu gọn tối đa các cột phụ (KH, mã đơn, ngày, nơi/số giảm trừ, trạng thái)
  // để DÀNH CHỖ cho "Lý do hoàn" (viết dài) + "Tài khoản nhận" (150px, đủ hiện
  // ngân hàng + STK + tên; bảng cuộn ngang nếu màn hẹp).
  const COLS = [76, 116, 112, 84, 300, 84, 78, 62, 150, 86, 44];
  const nDone = data.items.filter((p) => p.applied).length;
  const nUndone = data.items.length - nDone;
  const nByStatus = data.items.reduce((a, p) => { a[p.status] = (a[p.status] || 0) + 1; return a; }, {});
  const shown = data.items
    .filter((p) => (filter === 'done' ? p.applied : filter === 'undone' ? !p.applied : true))
    .filter((p) => !statusFilter || p.status === statusFilter);
  // Đang lọc theo trạng thái = chế độ soát duyệt -> ẩn dòng nhập nháp + nút gửi cho gọn.
  const showDrafts = filter !== 'done' && !statusFilter;

  return (
    <div className="page">
      <div className="page-head" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Đề xuất hoàn tiền</h2>
        <span className="small muted">{data.canSeeAll ? 'Toàn bộ' : 'Của tôi'} · {data.items.length} đề xuất</span>
        <div className="row" style={{ gap: 4 }}>
          {[['all', `Tất cả (${data.items.length})`], ['undone', `Chưa giảm trừ (${nUndone})`], ['done', `Đã giảm trừ (${nDone})`]].map(([v, l]) => (
            <button key={v} className={'btn sm' + (filter === v ? '' : ' ghost')} onClick={() => setFilter(v)}>{l}</button>
          ))}
        </div>
        {/* Lọc theo trạng thái duyệt — TP bấm đúng nút "Chờ TP …" của mình để soát nhanh */}
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {Object.entries(STATUS).map(([v, s]) => (
            <button
              key={v}
              className={'btn sm' + (statusFilter === v ? '' : ' ghost')}
              style={statusFilter === v ? undefined : { color: s.c }}
              title={statusFilter === v ? 'Bấm lại để bỏ lọc' : `Chỉ hiện đề xuất "${s.l}"`}
              onClick={() => setStatusFilter((cur) => (cur === v ? '' : v))}
            >
              {s.l} ({nByStatus[v] || 0})
            </button>
          ))}
        </div>
        <div className="spacer" />
        {data.isKeToanHead && <button className="btn" onClick={openApply}>⚖️ Giảm trừ cả bảng</button>}
      </div>

      {/* Nút gửi đặt NGAY TRÊN bảng — dòng nhập đề xuất giờ nằm đầu bảng, khỏi cuộn xuống cuối. */}
      {showDrafts && (
        <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 10 }}>
          <button className="btn" disabled={busy} onClick={submit}>{busy ? 'Đang gửi…' : 'Gửi đề xuất'}</button>
        </div>
      )}

      {/* MOBILE: danh sách đề xuất dạng card (xem + trạng thái). Tạo/sửa/duyệt dùng máy tính. */}
      <div className="only-mobile mlist">
        {shown.length ? shown.map((p) => {
          const st = STATUS[p.status] || { l: p.status, c: 'var(--muted)' };
          return (
            <div key={p.id} className="mlist-card" style={{ cursor: 'default' }}>
              <span className="mlist-main">
                <span className="mlist-title"><code style={{ color: 'var(--warn)', fontSize: 12 }}>{p.custCode || '—'}</code> {p.custName || ''}</span>
                <span className="mlist-sub">{p.wholeMonth ? 'Cả tháng' : (p.orderCode || '—')} · {fmtVND(p.amount)}</span>
              </span>
              <span className="mlist-badge" style={{ background: `color-mix(in srgb, ${st.c} 14%, transparent)`, color: st.c }}>{st.l}</span>
            </div>
          );
        }) : <div className="empty">Chưa có đề xuất nào.</div>}
      </div>

      <div className="card only-desktop" style={{ overflowX: 'auto' }}>
        <table style={{ fontSize: 12, minWidth: 1192, width: '100%', tableLayout: 'fixed' }}>
          <colgroup>{COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <thead><tr>
            <th style={th}>Nhân viên</th><th style={th}>Khách hàng (mã · tên)</th><th style={th}>Mã đơn hàng</th>
            <th style={th}>Ngày</th><th style={th}>Lý do hoàn</th><th style={thR}>Số tiền</th><th style={th}>Nơi giảm trừ</th>
            <th style={thR}>Số giảm trừ</th><th style={th}>Tài khoản nhận</th><th style={th}>Trạng thái</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {/* Dòng nhập mới Ở ĐẦU BẢNG cho tiện — tự thêm dòng khi điền hết (ẩn khi lọc "đã giảm trừ"/trạng thái) */}
            {showDrafts && drafts.map((d, i) => {
              const rateDisabled = !(d.reduceTarget === 'congMua' || d.reduceTarget === 'cuoc');
              return (
                <tr key={`d${i}`} style={{ borderBottom: '1px solid var(--line)', background: 'color-mix(in srgb, var(--accent) 4%, transparent)' }}>
                  <td style={td}><span className="small muted">{me.name}</span></td>
                  <td style={td}>
                    <CustomerPicker code={d.custCode} name={d.custName} onPick={(v) => setDraft(i, v)} />
                  </td>
                  <td style={td}>
                    {d.wholeMonth
                      ? <span className="tag" style={{ background: 'color-mix(in srgb, var(--warn-fill) 18%, transparent)', color: 'var(--warn)', cursor: 'pointer' }} onClick={() => setDraft(i, { wholeMonth: false })}>Cả tháng ✕</span>
                      : <span className="row" style={{ gap: 4, flexWrap: 'nowrap' }}><input value={d.orderCode} onChange={(e) => setDraft(i, { orderCode: e.target.value })} placeholder="Mã đơn" style={{ ...inp, minWidth: 0 }} /><button className="btn sm ghost" type="button" style={{ whiteSpace: 'nowrap', flex: 'none' }} onClick={() => setDraft(i, { wholeMonth: true, orderCode: '' })}>Cả tháng</button></span>}
                  </td>
                  <td style={td}>{d.wholeMonth
                    ? <input type="month" value={d.month} onChange={(e) => setDraft(i, { month: e.target.value })} style={inp} />
                    : <span className="small muted" title="Tự suy từ mã đơn">tự động</span>}
                  </td>
                  <td style={{ ...td, verticalAlign: 'top' }}>
                    {/* Tối thiểu 4 dòng; viết dài hơn thì tự cao thêm để hiện đủ, không cần cuộn. */}
                    <textarea
                      value={d.reason}
                      onChange={(e) => setDraft(i, { reason: e.target.value })}
                      onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = Math.max(REASON_MIN_H, t.scrollHeight) + 'px'; }}
                      placeholder="Lý do"
                      rows={4}
                      style={{ ...inp, minHeight: REASON_MIN_H, lineHeight: 1.45, resize: 'vertical', overflow: 'hidden' }}
                    />
                  </td>
                  <td style={td}><input value={d.amount} onChange={(e) => setDraft(i, { amount: e.target.value.replace(/[^\d]/g, '') })} placeholder="0" style={{ ...inp, textAlign: 'right' }} /></td>
                  <td style={td}><select value={d.reduceTarget} onChange={(e) => setDraft(i, { reduceTarget: e.target.value, reduceRate: 0 })} style={inp}>{TARGETS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}</select></td>
                  <td style={tdR}>{rateCell(d, i, rateDisabled)}</td>
                  <td style={{ ...td, verticalAlign: 'top' }}><textarea value={d.account} onChange={(e) => setDraft(i, { account: e.target.value })}
                    onInput={(e) => { const t = e.currentTarget; t.style.height = 'auto'; t.style.height = Math.max(38, t.scrollHeight) + 'px'; }}
                    placeholder="Ví KH / STK" rows={2} style={{ ...inp, minHeight: 38, lineHeight: 1.4, resize: 'vertical', overflow: 'hidden' }} /></td>
                  <td style={td}><span className="small muted">nháp</span></td>
                  <td style={td}>{drafts.length > 1 && <button className="btn sm ghost" type="button" onClick={() => removeDraft(i)}>×</button>}</td>
                </tr>
              );
            })}
            {shown.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid var(--line)' }}>
                <td style={td}>{p.createdByName}</td>
                <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${p.custCode || ''} ${p.custName || ''}`.trim()}>
                  <code style={{ color: 'var(--warn)' }}>{p.custCode || '—'}</code> <span>{p.custName}</span>
                </td>
                <td style={{ ...td, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.wholeMonth ? <span className="tag" style={{ background: 'color-mix(in srgb, var(--warn-fill) 18%, transparent)', color: 'var(--warn)' }}>Cả tháng</span> : <code>{p.orderCode || '—'}</code>}</td>
                <td style={td} title={`Tháng giảm trừ: ${p.month}`}><span className="small muted">{fmtDay(p.createdAt)}</span></td>
                <td style={{ ...td, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.reason || <span className="muted">—</span>}</td>
                <td style={{ ...tdR, fontFamily: 'var(--font-mono)' }}>{fmtVND(p.amount)}</td>
                {canEditReduction(p) ? (
                  <>
                    <td style={td}>
                      <select value={p.reduceTarget} style={{ width: '100%', boxSizing: 'border-box' }}
                        onChange={(e) => saveReduction(p, { reduceTarget: e.target.value })}>
                        {TARGETS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                      </select>
                    </td>
                    <td style={tdR}>
                      {p.reduceTarget === 'congMua' ? (
                        <select value={p.reduceRate} style={{ width: '100%', boxSizing: 'border-box' }} onChange={(e) => saveReduction(p, { reduceRate: Number(e.target.value) })}>
                          <option value={0}>%</option>{PCT.map((x) => <option key={x} value={x}>{x}%</option>)}
                        </select>
                      ) : p.reduceTarget === 'cuoc' && p.reduceRate > 0 ? (
                        <select value={p.reduceRate} style={{ width: '100%', boxSizing: 'border-box' }} onChange={(e) => saveReduction(p, { reduceRate: Number(e.target.value) })}>
                          {KG.map((x) => <option key={x} value={x}>{x}k/kg</option>)}
                          <option value={0}>— theo số tiền —</option>
                        </select>
                      ) : p.reduceTarget === 'khac' ? (
                        <span className="muted">—</span>
                      ) : (
                        // Tiền hàng / Phụ thu / Cước-không-k/kg: trừ theo SỐ TIỀN -> ô nhập tiền.
                        // Mặc định bằng "Số tiền" (tiền trả khách); sửa khi số trừ doanh thu KHÁC nó
                        // (vd tiền hoàn đã gồm phí dịch vụ, mà phí dịch vụ không nằm trong doanh thu).
                        <MoneyCell
                          value={p.reduceAmount == null ? p.amount : p.reduceAmount}
                          onSave={(v) => saveReduction(p, { reduceAmount: v })}
                          extra={p.reduceTarget === 'cuoc' ? (
                            <select value={0} style={{ width: '100%', boxSizing: 'border-box', marginTop: 3 }} onChange={(e) => saveReduction(p, { reduceRate: Number(e.target.value) })}>
                              <option value={0}>theo số tiền</option>{KG.map((x) => <option key={x} value={x}>{x}k/kg</option>)}
                            </select>
                          ) : null}
                        />
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td style={td}>{TLABEL[p.reduceTarget]}</td>
                    <td style={tdR}>{p.reduceTarget === 'congMua' ? `${p.reduceRate}%` : p.reduceTarget === 'cuoc' ? `${p.reduceRate}k/kg` : <span className="muted">0</span>}</td>
                  </>
                )}
                <td style={{ ...td, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{p.account || <span className="muted">—</span>}</td>
                <td style={td}>
                  <span style={{ color: STATUS[p.status]?.c, fontWeight: 600 }}>{STATUS[p.status]?.l}</span>
                  {p.applied && <div className="small" style={{ color: 'var(--ok)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }} onClick={() => setDetail(p)} title="Xem đã trừ đơn nào">✓ Đã giảm trừ{p.appliedOrders?.length ? ` (${p.appliedOrders.length} đơn)` : ''} ›</div>}
                  {p.status === 'rejected' && p.rejected?.reason && <div className="small muted">{p.rejected.reason}</div>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {canApprove(p) && <><button className="btn sm ok" onClick={() => doApprove(p)}>Duyệt</button> <button className="btn sm ghost danger" onClick={() => doReject(p)}>Từ chối</button></>}
                  {data.isKeToanHead && p.status === 'approved' && !p.applied && p.reduceTarget !== 'khac' && <button className="btn sm ghost" onClick={() => doReassign(p)} title="Đơn gốc đã chốt — chỉ định đơn khác">Chỉ định đơn</button>}
                  {(isAdmin || (String(p.createdBy) === String(me.id || me._id) && p.status !== 'approved')) && !p.applied && <button className="btn sm ghost" onClick={() => doDelete(p)} title="Xóa">×</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={() => setConfirmOpen(false)}>
          <div className="card" style={{ maxWidth: 560, width: '92%', maxHeight: '86vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>⚖️ Giảm trừ vào bảng doanh thu</h3>

            {applyResult ? (
              // ── KẾT QUẢ sau khi giảm trừ ──
              <>
                <p style={{ lineHeight: 1.6 }}>
                  Tháng <b>{applyResult.month}</b>: đã giảm trừ <b style={{ color: 'var(--ok)' }}>{applyResult.applied}</b> đề xuất
                  {applyResult.virtual ? ` (${applyResult.virtual} đơn ảo)` : ''}.
                  {applyResult.skipped?.length ? <><br />Bỏ qua <b style={{ color: 'var(--danger)' }}>{applyResult.skipped.length}</b> đơn (đã trừ trước / không có trong doanh thu):</> : null}
                </p>
                {applyResult.skipped?.length ? (
                  <div style={{ maxHeight: 200, overflowY: 'auto', fontSize: 12, border: '1px solid var(--line)', borderRadius: 8, padding: 8 }}>
                    {applyResult.skipped.map((s, i) => (
                      <div key={i}>• KH <code style={{ color: 'var(--warn)' }}>{s.custCode}</code> {s.orderCode ? <>đơn <code>{s.orderCode}</code></> : '(cả tháng)'} — {s.reason}</div>
                    ))}
                  </div>
                ) : null}
                <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                  <button className="btn" onClick={() => setConfirmOpen(false)}>Đóng</button>
                </div>
              </>
            ) : (
              // ── CHỌN THÁNG + TÍCH ĐƠN ──
              <>
                <div className="row" style={{ gap: 10, alignItems: 'center', margin: '4px 0 10px' }}>
                  <span className="small muted">Tháng giảm trừ:</span>
                  <select value={applyMonth} onChange={(e) => loadApplyMonth(e.target.value)} style={{ width: 'auto' }}>
                    {(applyMonths.length ? applyMonths : [applyMonth || thisMonth()]).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  {applyScan === 'loading' ? <span className="small muted">đang kiểm tra…</span>
                    : applyScan === 'done' ? <span className="small" style={{ color: 'var(--ok)' }}>✓ đã quét doanh thu</span>
                    : <span className="small" style={{ color: 'var(--danger)' }}>⚠ chưa quét doanh thu tháng này</span>}
                </div>

                {applyScan !== 'done' && (
                  <div className="small" style={{ color: 'var(--danger)', marginBottom: 10 }}>
                    Tháng <b>{applyMonth}</b> chưa quét xong bảng doanh thu tính lương. Vào <b>Bảng lương → Quét doanh thu</b> tháng này trước, rồi mới giảm trừ được.
                  </div>
                )}

                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
                  <table style={{ width: '100%', fontSize: 12 }}>
                    <thead><tr style={{ position: 'sticky', top: 0, background: 'var(--panel2)' }}>
                      <th style={{ ...th, width: 34, textAlign: 'center' }}>
                        <input type="checkbox" style={{ width: 'auto' }}
                          checked={applyRows.length > 0 && applyRows.every((p) => applyPick.has(p.id))}
                          onChange={(e) => setApplyPick(e.target.checked ? new Set(applyRows.map((p) => p.id)) : new Set())} />
                      </th>
                      <th style={th}>Khách hàng</th><th style={th}>Mã đơn</th><th style={th}>Giảm trừ</th><th style={thR}>Số tiền</th>
                    </tr></thead>
                    <tbody>
                      {applyRows.length ? applyRows.map((p) => (
                        <tr key={p.id} style={{ borderTop: '1px solid var(--line)' }}>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <input type="checkbox" style={{ width: 'auto' }} checked={applyPick.has(p.id)} onChange={() => togglePick(p.id)} />
                          </td>
                          <td style={td}><code style={{ color: 'var(--warn)' }}>{p.custCode}</code> {p.custName}</td>
                          <td style={td}>{p.wholeMonth ? 'Cả tháng' : <code>{p.orderCode || '—'}</code>}</td>
                          <td style={td}>{TLABEL[p.reduceTarget]}{p.reduceTarget === 'congMua' ? ` ${p.reduceRate}%` : p.reduceTarget === 'cuoc' && p.reduceRate ? ` ${p.reduceRate}k/kg` : ''}</td>
                          <td style={{ ...tdR, fontFamily: 'var(--font-mono)' }}>{fmtVND(p.amount)}</td>
                        </tr>
                      )) : <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--muted)' }}>Không có đề xuất đã duyệt chưa giảm trừ ở tháng này.</td></tr>}
                    </tbody>
                  </table>
                </div>

                <label className="row" style={{ gap: 8, alignItems: 'flex-start', margin: '12px 0 4px' }}>
                  <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} />
                  <span className="small">Tôi xác nhận <b>đã quét đủ doanh thu tháng {applyMonth}</b>. <span style={{ color: 'var(--danger)' }}>Nếu quét lại doanh thu sau khi giảm trừ, số đã trừ sẽ bị ghi đè.</span></span>
                </label>
                <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  <button className="btn ghost" onClick={() => setConfirmOpen(false)}>Hủy</button>
                  <button className="btn" disabled={!confirmChecked || applyScan !== 'done' || !applyPick.size || busy} onClick={doApply}>
                    {busy ? 'Đang giảm trừ…' : `Giảm trừ ${applyPick.size} đơn`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={() => setDetail(null)}>
          <div className="card" style={{ maxWidth: 520, width: '90%', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>✓ Đã giảm trừ những đơn nào</h3>
            <p className="small muted" style={{ marginTop: 0 }}>
              KH <code style={{ color: 'var(--warn)' }}>{detail.custCode || '—'}</code> {detail.custName} · tháng {detail.month}
              {detail.appliedBy ? ` · ${detail.appliedBy}` : ''}{detail.appliedAt ? ` · ${new Date(detail.appliedAt).toLocaleString('vi-VN')}` : ''}
            </p>
            {detail.appliedOrders?.length ? (
              <table style={{ width: '100%', fontSize: 12 }}>
                <thead><tr><th style={{ ...th }}>Mã đơn</th><th style={{ ...th }}>Đã trừ</th></tr></thead>
                <tbody>
                  {detail.appliedOrders.map((o, k) => (
                    <tr key={k} style={{ borderTop: '1px solid var(--line)' }}>
                      <td style={td}><code>{o.billCode}</code></td>
                      <td style={td}>{o.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="small muted">Loại "Khác" — không giảm trừ vào doanh thu.</p>}
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn ghost" onClick={() => setDetail(null)}>Đóng</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
